"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { FaArrowRight } from "react-icons/fa";
import ColumnCard, { ColumnCardProps } from "./ColumnCard";
import { apiV2Client } from "@/lib/api-v2-client";

interface ColumnsSectionProps {
  title?: string;
  subtitle?: string;
  showFeaturedOnly?: boolean;
  limit?: number;
  showMoreLink?: boolean;
  className?: string;
}

interface ColumnsResponse {
  results: ColumnCardProps[];
  count: number;
}

export default function ColumnsSection({
  title = "転職お役立ちコラム",
  subtitle = "転職活動やキャリアアップに役立つ情報をお届けします",
  showFeaturedOnly = false,
  limit = 4,
  showMoreLink = true,
  className = "",
}: ColumnsSectionProps) {
  const [columns, setColumns] = useState<ColumnCardProps[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchColumns = async () => {
      try {
        const params = new URLSearchParams();
        params.append("limit", String(limit));
        if (showFeaturedOnly) {
          params.append("is_featured", "true");
        }

        const response = await apiV2Client.get<ColumnsResponse>(
          `/api/v2/public/columns/?${params.toString()}`
        );
        setColumns(response.data.results || []);
      } catch (err) {
        console.error("Failed to fetch columns:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchColumns();
  }, [limit, showFeaturedOnly]);

  if (loading) {
    return (
      <section className={`py-12 ${className}`}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-48 mx-auto mb-4" />
              <div className="h-4 bg-gray-200 rounded w-64 mx-auto" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (columns.length === 0) {
    return null;
  }

  return (
    <section className={`py-16 bg-gradient-to-b from-orange-50/50 to-white ${className}`}>
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-xs font-bold text-[#ed7243] tracking-[0.3em] uppercase mb-3">Column</p>
          <h2 className="text-2xl md:text-3xl font-bold text-[#32291f] mb-3">{title}</h2>
          {subtitle && <p className="text-[#32291f]/60">{subtitle}</p>}
        </div>

        {/* Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {columns.map((column) => (
            <ColumnCard key={column.id} {...column} />
          ))}
        </div>

        {/* More Link */}
        {showMoreLink && (
          <div className="text-center mt-10">
            <Link
              href="/columns"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white border-2 border-[#ed7243] text-[#ed7243] rounded-full font-bold hover:bg-[#ed7243] hover:text-white transition-all duration-300"
            >
              コラム一覧を見る
              <FaArrowRight />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
